<script setup>
import { ref } from 'vue';
import { useStore } from '../store.js';
import api from '../ApiClient.js';
import FormGroup from './FormGroup.vue';

const { createMovie, store } = useStore();

const step = ref(1);
const form = ref({
  name: '',
  prompt: '',
  script: ''
});
const jobId = ref(null);
const createdMovie = ref(null);
const error = ref('');

// Add selection for prompt or script
const inputType = ref('prompt'); // 'prompt' or 'script'

async function handleSubmit() {
  error.value = '';
  try {
    const payload = { name: form.value.name };
    if (inputType.value === 'prompt') {
      payload.prompt = form.value.prompt;
    } else {
      payload.script = form.value.script;
    }
    const result = await createMovie(payload);
    if (result && result.jobId) {
      jobId.value = result.jobId;
      step.value = 2;
      // Poll for job completion
      pollJob(result.movie);
    } else if (result && result.movie && result.movie.id) {
      // Fallback: movie created instantly
      createdMovie.value = result;
      step.value = 3;
      document.location.hash = 'edit/' + result.movie.id;
    }
  } catch (e) {
    error.value = e.message || 'Failed to create movie.';
  }
}

async function pollJob(movie) {
  if (!jobId.value) return;
  const poll = setInterval(async () => {
    let job;
    try {
      job = await api.getJobStatus(jobId.value); // Always fetch from server
    } catch (e) {
      error.value = 'Failed to fetch job status.';
      clearInterval(poll);
      step.value = 1;
      return;
    }
    if (job && job.status === 'done') {
      clearInterval(poll);
      createdMovie.value = movie.id;
      step.value = 3;
      document.location.hash = 'edit/' + movie.id;
    } else if (job && job.status === 'error') {
      clearInterval(poll);
      error.value = job.error || 'Job failed.';
      step.value = 1;
    }
  }, 1500);
}
</script>

<template>
  <div class="new-movie-wizard">
    <div v-if="step === 1" class="modal without-overlay">
      <div class="modal-header"><h3>Create a New Movie</h3></div>
      <div class="modal-body">
        <form @submit.prevent="handleSubmit">
          <FormGroup label="Name" for="name" description="The title of your movie.">
            <input id="name" v-model="form.name" required />
          </FormGroup>
          <FormGroup for="inputType">
            <select id="inputType" v-model="inputType">
              <option value="manual">I will enter the data manually</option>
              <option value="prompt">Start with a prompt</option>
              <option value="script">Start with the entire script</option>
            </select>
          </FormGroup>
          <FormGroup v-if="inputType === 'prompt'" label="Prompt" for="prompt" description="Describe your movie idea.">
            <textarea id="prompt" v-model="form.prompt" rows="3" required></textarea>
          </FormGroup>
          <FormGroup v-if="inputType === 'script'" label="Script" for="script" description="Paste your full script.">
            <textarea id="script" v-model="form.script" rows="6" required></textarea>
          </FormGroup>
          <div v-if="error" class="error">{{ error }}</div>

          <div class="form-actions">
            <button type="submit" :disabled="store.loading">Create Movie</button>
          </div>
        </form>
      </div>
    </div>
    <div v-else-if="step === 2">
      <h2>Creating Movie...</h2>
      <p>Your movie is being generated. This may take a moment.</p>
      <div class="loading">Waiting for job to complete...</div>
    </div>
  </div>
</template>

<style scoped>
textarea {
  height: 30vh;
}
</style>
